import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import AddTaskForm from './AddTask';
import { useAuth } from '../context/AuthContext';
import { fetchTasks, fetchAllTasks, fetchBoardTasks, updateTask, type TaskRow } from '../api/tasks';
import { fetchClients, type ClientRow } from '../api/clients';
import { fetchOrdersForUser, type OrderRow } from '../api/orderApi';
import { TAG_PILL_COLORS, ROLE_LABELS as TAG_LABELS } from '../constants/taskTags';
import { formatTaskDate, isTaskOverdue } from '../utils/formatTaskDate';
import { mergeStatusFilterOptions, taskMatchesStatusFilter } from '../utils/taskStatusFilter';
import { TaskStatusPills } from '../components/TaskStatusPills';
import { SortableTh, SortHeaderButton } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import { displayTaskPriority, userHasRiedRole } from '../utils/taskPriorityDisplay';
import './Page.css';
import './TasksBoard.css';

const PRIORITY_PILL_COLORS: Record<string, string> = {
  Low: '#73BF4380',
  Medium: '#FFC10780',
  High: '#E48B5280',
  Urgent: '#ef444480',
};

/** Same colors as the order status badges, so a Pilot order reads as Pilot on the board. */
const ORDER_STATUS_PILL_COLORS: Record<string, { background: string; color: string }> = {
  Pending: { background: '#F9C39DBF', color: '#7C3512' },
  'In Progress': { background: '#dbeafe', color: '#2A425F' },
  Approved: { background: '#A8F7A9BF', color: '#1F4B31' },
  Pilot: { background: '#E4D4F7', color: '#4C1D95' },
};

/** Sort key: first assignee only (not the full comma-separated group label). */
function sortKeyFirstAssignee(task: TaskRow): string {
  const first = task.assignees?.[0]?.name?.trim();
  if (first) return first;
  const joined = task.assigned_to_name?.trim();
  if (!joined) return '';
  return joined.split(',')[0]?.trim() ?? '';
}

function matchesSearch(task: TaskRow, search: string): boolean {
  if (!search.trim()) return true;
  const term = search.trim().toLowerCase();
  const idStr = String(task.id);
  const nameMatch = task.name.toLowerCase().includes(term);
  const idMatch = idStr.includes(term) || (term.startsWith('#') && idStr.includes(term.slice(1)));
  const tagsMatch = (task.tags ?? []).some(
    (tag) =>
      tag.toLowerCase().includes(term) ||
      tag.replace(/_/g, ' ').toLowerCase().includes(term) ||
      term.replace(/\s+/g, '_').includes(tag.toLowerCase())
  );
  const assigneeMatch =
    (task.assignees ?? []).some((a) => a.name.toLowerCase().includes(term)) ||
    (task.assigned_to_name?.toLowerCase().includes(term) ?? false);
  const descMatch = (task.description ?? '').toLowerCase().includes(term);
  const notesMatch = (task.notes ?? '').toLowerCase().includes(term);
  const assignerMatch = (task.assigner_name ?? '').toLowerCase().includes(term);
  return nameMatch || idMatch || tagsMatch || assigneeMatch || descMatch || notesMatch || assignerMatch;
}

type BoardColumn = 'todo' | 'inProgress' | 'unassigned' | 'onHold' | 'watched';

/** Overdue list: only tasks the user created (assigner) or is assigned to. */
function taskIsMineForBoardOverdue(task: TaskRow, userId: number): boolean {
  if (task.assigner_id === userId) return true;
  if (task.assigned_to_user_id === userId) return true;
  return (task.assignees ?? []).some((a) => a.user_id === userId);
}

/** True only when the user is actually doing the task, ignoring tasks they merely created. */
function taskIsAssignedToUser(task: TaskRow, userId: number): boolean {
  if (task.assigned_to_user_id === userId) return true;
  return (task.assignees ?? []).some((a) => a.user_id === userId);
}

function taskHasAssignees(task: TaskRow): boolean {
  return (
    (task.assignees?.length ?? 0) > 0 ||
    (task.assigned_to_user_id != null && Number.isInteger(task.assigned_to_user_id))
  );
}

const TasksBoard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorTask = user?.id != null ? { acting_user_id: user.id } : {};
  const hasRied = useMemo(
    () => Boolean(user?.id) && userHasRiedRole(user?.roles, user?.role),
    [user?.id, user?.roles, user?.role]
  );
  const [todo, setTodo] = useState<TaskRow[]>([]);
  const [inProgress, setInProgress] = useState<TaskRow[]>([]);
  const [unassigned, setUnassigned] = useState<TaskRow[]>([]);
  const [onHold, setOnHold] = useState<TaskRow[]>([]);
  const [watched, setWatched] = useState<TaskRow[]>([]);
  const [completed, setCompleted] = useState<TaskRow[]>([]);
  const [ordersForUser, setOrdersForUser] = useState<OrderRow[]>([]);
  const [assignedCompanies, setAssignedCompanies] = useState<ClientRow[]>([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    taskBoard: false,
    assignedCompanies: false,
    completedTasks: false,
    allTasks: false,
  });
  const [boardTasks, setBoardTasks] = useState<TaskRow[]>([]);
  const [boardTasksError, setBoardTasksError] = useState('');
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Drag-and-drop state
  const [draggedTask, setDraggedTask] = useState<TaskRow | null>(null);
  const [draggedFrom, setDraggedFrom] = useState<BoardColumn | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<BoardColumn | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<number | null>(null);
  const [dragInsertAfter, setDragInsertAfter] = useState(false);
  const isDraggingRef = useRef(false);

  // Order drag-and-drop (visual only — does not change order status)
  const [draggedOrder, setDraggedOrder] = useState<OrderRow | null>(null);
  const [draggedOrderFrom, setDraggedOrderFrom] = useState<'todo' | 'inProgress' | null>(null);
  const [dragOverOrderNumber, setDragOverOrderNumber] = useState<string | null>(null);
  const [dragInsertAfterOrder, setDragInsertAfterOrder] = useState(false);
  const [orderColumnOverrides, setOrderColumnOverrides] = useState<Record<string, 'todo' | 'inProgress'>>({});

  // Assign modal state
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [pendingDropTarget, setPendingDropTarget] = useState<'todo' | 'inProgress' | null>(null);

  // Confirm unassign modal state
  const [confirmUnassignOpen, setConfirmUnassignOpen] = useState(false);

  // Drag error
  const [dragError, setDragError] = useState('');
  const [allTasks, setAllTasks] = useState<TaskRow[]>([]);

  // All Tasks column filters
  const [allTasksFilters, setAllTasksFilters] = useState<Record<string, string[]>>({
    status: [],
    priority: [],
    tags: [],
    assigned_to: [],
    assigned_by: [],
    company: [],
    project: [],
    due_date: [],
  });
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);
  const [filterDropdownPos, setFilterDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const searchSort = useTableSortState();
  const boardTableSort = useTableSortState();
  const completedSectionSort = useTableSortState();
  const allTasksSort = useTableSortState();
  const assignedCompaniesSort = useTableSortState();

  const toggleFilterValue = (col: string, value: string) =>
    setAllTasksFilters((prev) => {
      const cur = prev[col];
      return { ...prev, [col]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });

  const allTasksUniqueValues = useMemo(() => ({
    status: mergeStatusFilterOptions(allTasks.map((t) => t.status)),
    priority: Array.from(new Set(allTasks.map((t) => displayTaskPriority(t, hasRied) ?? '—'))).filter(Boolean).sort(),
    tags: Array.from(new Set(allTasks.flatMap((t) => t.tags ?? []))).sort(),
    assigned_to: Array.from(
      new Set(
        allTasks.flatMap((t) => {
          const parts = t.assignees?.length
            ? t.assignees.map((a) => a.name.trim()).filter(Boolean)
            : t.assigned_to_name
              ? t.assigned_to_name.split(',').map((s) => s.trim()).filter(Boolean)
              : [];
          if (parts.length > 0) return parts;
          return ['—'];
        })
      )
    ).sort(),
    assigned_by: Array.from(new Set(allTasks.map((t) => t.assigner_name ?? '—'))).sort(),
    company: Array.from(new Set(allTasks.map((t) => t.client_company ?? '—'))).sort(),
    project: Array.from(new Set(allTasks.map((t) => (t.project_name && t.project_name.trim()) || '—'))).sort(
      (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })
    ),
    due_date: Array.from(new Set(allTasks.map((t) => formatTaskDate(t.due_date)))).sort((a, b) => {
      if (a === '—') return 1;
      if (b === '—') return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    }),
  }), [allTasks, hasRied]);

  const filteredAllTasks = useMemo(() => {
    const { status, priority, tags, assigned_to, assigned_by, company, project, due_date } = allTasksFilters;
    return allTasks.filter((task) => {
      if (status.length > 0 && !taskMatchesStatusFilter(task, status)) return false;
      if (priority.length > 0 && !priority.includes(displayTaskPriority(task, hasRied) ?? '—')) return false;
      if (tags.length > 0 && !tags.some((t) => (task.tags ?? []).includes(t))) return false;
      if (assigned_to.length > 0) {
        const nameParts = task.assignees?.length
          ? task.assignees.map((a) => a.name.trim()).filter(Boolean)
          : task.assigned_to_name
            ? task.assigned_to_name.split(',').map((s) => s.trim()).filter(Boolean)
            : [];
        const matches =
          nameParts.some((n) => assigned_to.includes(n)) ||
          (nameParts.length === 0 && assigned_to.includes('—'));
        if (!matches) return false;
      }
      if (assigned_by.length > 0 && !assigned_by.includes(task.assigner_name ?? '—')) return false;
      if (company.length > 0 && !company.includes(task.client_company ?? '—')) return false;
      if (project.length > 0) {
        const label = (task.project_name && task.project_name.trim()) || '—';
        if (!project.includes(label)) return false;
      }
      if (due_date.length > 0) {
        const label = formatTaskDate(task.due_date);
        if (!due_date.includes(label)) return false;
      }
      return true;
    });
  }, [allTasks, allTasksFilters, hasRied]);

  const sortedFilteredAllTasks = useMemo(
    () =>
      sortRows(filteredAllTasks, allTasksSort.sortKey, allTasksSort.sortDir, (task, key) => {
        switch (key) {
          case 'name':
            return task.name ?? '';
          case 'status':
            return `${task.status ?? ''}\t${isTaskOverdue(task) ? '0' : '1'}`;
          case 'priority':
            return displayTaskPriority(task, hasRied) ?? '';
          case 'tags':
            return (task.tags ?? []).join(', ');
          case 'assigned_to':
            return sortKeyFirstAssignee(task);
          case 'assigned_by':
            return task.assigner_name ?? '';
          case 'company':
            return task.client_company ?? '';
          case 'project':
            return task.project_name ?? '';
          case 'due_date':
            return task.due_date ?? '';
          default:
            return '';
        }
      }),
    [filteredAllTasks, allTasksSort.sortKey, allTasksSort.sortDir, hasRied]
  );

  const handleFilterBtnClick = (col: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (openFilterCol === col) {
      setOpenFilterCol(null);
      setFilterDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterDropdownPos({ top: rect.bottom + 6, left: rect.left });
    setOpenFilterCol(col);
  };

  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('admin');

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setTodo([]);
      setInProgress([]);
      setUnassigned([]);
      setOnHold([]);
      setWatched([]);
      setCompleted([]);
      setOrdersForUser([]);
      setAssignedCompanies([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      fetchTasks(user.id),
      fetchClients('client', user.id),
      fetchOrdersForUser(user.id),
    ])
      .then(([sections, clients, orders]) => {
        if (cancelled) return;
        setTodo(sections.todo);
        setInProgress(sections.inProgress);
        setUnassigned(sections.unassigned);
        setOnHold(sections.onHold ?? []);
        setWatched(sections.watched ?? []);
        setCompleted(sections.completed);
        setOrdersForUser(orders);
        setAssignedCompanies(clients);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tasks.');
          setTodo([]);
          setInProgress([]);
          setUnassigned([]);
          setOnHold([]);
          setWatched([]);
          setCompleted([]);
          setOrdersForUser([]);
          setAssignedCompanies([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!isAdmin || !user?.id) {
      setAllTasks([]);
      return;
    }
    let cancelled = false;
    fetchAllTasks(user.id)
      .then((list) => {
        if (!cancelled) setAllTasks(list);
      })
      .catch(() => {
        if (!cancelled) setAllTasks([]);
      });
    return () => { cancelled = true; };
  }, [isAdmin, user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setBoardTasks([]);
      setBoardTasksError('');
      return;
    }
    let cancelled = false;
    fetchBoardTasks(user.id)
      .then((list) => {
        if (!cancelled) {
          setBoardTasks(list);
          setBoardTasksError('');
        }
      })
      .catch((e: unknown) => {
        // Surface the failure instead of rendering it as "no tasks" — an empty
        // table and a broken request used to look identical here.
        if (!cancelled) {
          setBoardTasks([]);
          setBoardTasksError(e instanceof Error ? e.message : 'Failed to load your task board.');
        }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!openFilterCol) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.all-tasks-filter-btn') && !target.closest('.all-tasks-filter-dropdown')) {
        setOpenFilterCol(null);
        setFilterDropdownPos(null);
      }
    };
    const closeOnScroll = (e: Event) => {
      if ((e.target as Element)?.closest?.('.all-tasks-filter-dropdown')) return;
      setOpenFilterCol(null);
      setFilterDropdownPos(null);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [openFilterCol]);

  const handleAddTask = () => {
    setAddTaskModalOpen(true);
  };

  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`, { state: { from: '/tasks' } });
  };

  const handleClientClick = (clientId: number) => {
    navigate(`/client/${clientId}`);
  };

  const handleOrderClick = (orderNumber: string) => {
    navigate(`/orders/${orderNumber}`);
  };

  // ── Drag-and-drop handlers ──────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, task: TaskRow, from: BoardColumn) => {
    isDraggingRef.current = true;
    setDraggedTask(task);
    setDraggedFrom(from);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleOrderDragStart = (e: React.DragEvent, order: OrderRow, from: 'todo' | 'inProgress') => {
    isDraggingRef.current = true;
    setDraggedOrder(order);
    setDraggedOrderFrom(from);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-task-board-type', 'order');
    e.dataTransfer.setData('application/x-order-number', order.order_number);
  };

  const handleDragEnd = () => {
    setDragOverColumn(null);
    setDragOverTaskId(null);
    setDraggedOrder(null);
    setDraggedOrderFrom(null);
    setDragOverOrderNumber(null);
    setTimeout(() => { isDraggingRef.current = false; }, 100);
  };

  // Column-level drag-over: fires when hovering over empty space in a column
  const handleDragOver = (e: React.DragEvent, column: BoardColumn) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column);
  };

  const handleOnHoldDragOver = (e: React.DragEvent) => {
    if (draggedOrder) return;
    handleDragOver(e, 'onHold');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDragOverColumn(null);
      setDragOverTaskId(null);
      setDragOverOrderNumber(null);
    }
  };

  // Task-level drag-over: determines insert position (above or below the hovered task)
  const handleTaskDragOver = (e: React.DragEvent, task: TaskRow, column: BoardColumn) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column);
    setDragOverTaskId(task.id);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDragInsertAfter(e.clientY > rect.top + rect.height / 2);
  };

  // Order-level drag-over: show drop indicator when dragging order over another order
  const handleOrderDragOver = (e: React.DragEvent, order: OrderRow, column: 'todo' | 'inProgress') => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedOrder) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column);
    setDragOverOrderNumber(order.order_number);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDragInsertAfterOrder(e.clientY > rect.top + rect.height / 2);
  };

  // Order drop: visual column override only (does not change order status)
  const handleOrderDrop = (e: React.DragEvent, targetColumn: 'todo' | 'inProgress') => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColumn(null);
    setDragOverOrderNumber(null);
    if (!draggedOrder || !draggedOrderFrom) return;
    if (draggedOrderFrom === targetColumn) {
      setDraggedOrder(null);
      setDraggedOrderFrom(null);
      return;
    }
    setOrderColumnOverrides((prev) => ({ ...prev, [draggedOrder.order_number]: targetColumn }));
    setDraggedOrder(null);
    setDraggedOrderFrom(null);
  };

  // Column-level drop: fires when dropping on empty space → appends to end
  const handleDrop = async (e: React.DragEvent, target: BoardColumn) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDragOverTaskId(null);
    setDragOverOrderNumber(null);

    if (target === 'watched' || draggedFrom === 'watched') {
      setDraggedTask(null);
      setDraggedFrom(null);
      setDraggedOrder(null);
      setDraggedOrderFrom(null);
      return;
    }

    if (draggedOrder) {
      if (target === 'onHold') {
        setDraggedOrder(null);
        setDraggedOrderFrom(null);
        return;
      }
      if (target === 'todo' || target === 'inProgress') handleOrderDrop(e, target);
      setDraggedOrder(null);
      setDraggedOrderFrom(null);
      return;
    }

    if (!draggedTask || !draggedFrom || draggedFrom === target) {
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    if (draggedFrom === 'unassigned' && (target === 'todo' || target === 'inProgress')) {
      setPendingDropTarget(target);
      setAssigneeModalOpen(true);
      return;
    }

    if (draggedFrom === 'unassigned' && target === 'onHold') {
      try {
        const updated = await updateTask(draggedTask.id, { status: 'On Hold', ...actorTask });
        setUnassigned((prev) => prev.filter((t) => t.id !== draggedTask.id));
        setOnHold((prev) => [...prev, updated]);
      } catch (err) {
        setDragError(err instanceof Error ? err.message : 'Failed to move task.');
        setTimeout(() => setDragError(''), 4000);
      }
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    if (draggedFrom === 'onHold' && (target === 'todo' || target === 'inProgress')) {
      if (!taskHasAssignees(draggedTask)) {
        setPendingDropTarget(target);
        setAssigneeModalOpen(true);
        return;
      }
      const newStatus = target === 'todo' ? 'To-Do' : 'In Progress';
      try {
        const updated = await updateTask(draggedTask.id, { status: newStatus, ...actorTask });
        setOnHold((prev) => prev.filter((t) => t.id !== draggedTask.id));
        if (target === 'todo') setTodo((prev) => [...prev, updated]);
        else setInProgress((prev) => [...prev, updated]);
      } catch (err) {
        setDragError(err instanceof Error ? err.message : 'Failed to move task.');
        setTimeout(() => setDragError(''), 4000);
      }
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    if (draggedFrom === 'onHold' && target === 'unassigned') {
      if (taskHasAssignees(draggedTask)) {
        setConfirmUnassignOpen(true);
        return;
      }
      try {
        const updated = await updateTask(draggedTask.id, { status: 'Unassigned', ...actorTask });
        setOnHold((prev) => prev.filter((t) => t.id !== draggedTask.id));
        setUnassigned((prev) => [...prev, updated]);
      } catch (err) {
        setDragError(err instanceof Error ? err.message : 'Failed to move task.');
        setTimeout(() => setDragError(''), 4000);
      }
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    if ((draggedFrom === 'todo' || draggedFrom === 'inProgress') && target === 'onHold') {
      try {
        const updated = await updateTask(draggedTask.id, { status: 'On Hold', ...actorTask });
        if (draggedFrom === 'todo') setTodo((prev) => prev.filter((t) => t.id !== draggedTask.id));
        else setInProgress((prev) => prev.filter((t) => t.id !== draggedTask.id));
        setOnHold((prev) => [...prev, updated]);
      } catch (err) {
        setDragError(err instanceof Error ? err.message : 'Failed to move task.');
        setTimeout(() => setDragError(''), 4000);
      }
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    if ((draggedFrom === 'todo' || draggedFrom === 'inProgress') && target === 'unassigned') {
      setConfirmUnassignOpen(true);
      return;
    }

    const newStatus = target === 'todo' ? 'To-Do' : 'In Progress';
    try {
      const updated = await updateTask(draggedTask.id, { status: newStatus, ...actorTask });
      if (draggedFrom === 'todo') {
        setTodo((prev) => prev.filter((t) => t.id !== draggedTask.id));
        setInProgress((prev) => [...prev, updated]);
      } else {
        setInProgress((prev) => prev.filter((t) => t.id !== draggedTask.id));
        setTodo((prev) => [...prev, updated]);
      }
    } catch (err) {
      setDragError(err instanceof Error ? err.message : 'Failed to move task.');
      setTimeout(() => setDragError(''), 4000);
    }
    setDraggedTask(null);
    setDraggedFrom(null);
  };

  // Task-level drop: respects the insert-before/after position
  const handleTaskDrop = (e: React.DragEvent, targetTask: TaskRow, targetColumn: BoardColumn) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColumn(null);
    setDragOverTaskId(null);
    setDragOverOrderNumber(null);

    if (targetColumn === 'watched' || draggedFrom === 'watched') {
      setDraggedTask(null);
      setDraggedFrom(null);
      setDraggedOrder(null);
      setDraggedOrderFrom(null);
      return;
    }

    if (draggedOrder) {
      if (targetColumn === 'onHold') {
        setDraggedOrder(null);
        setDraggedOrderFrom(null);
        return;
      }
      if (targetColumn === 'todo' || targetColumn === 'inProgress') handleOrderDrop(e, targetColumn);
      setDraggedOrder(null);
      setDraggedOrderFrom(null);
      return;
    }

    if (!draggedTask || !draggedFrom) return;
    if (draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    const after = dragInsertAfter;

    const insertInto = (
      setter: React.Dispatch<React.SetStateAction<TaskRow[]>>,
      item: TaskRow,
    ) => {
      setter((prev) => {
        const list = [...prev];
        const toIdx = list.findIndex((t) => t.id === targetTask.id);
        if (toIdx === -1) return [...list, item];
        list.splice(after ? toIdx + 1 : toIdx, 0, item);
        return list;
      });
    };

    // Cross-column modals (append at end — position inside the modal flow is less meaningful)
    if (draggedFrom === 'unassigned' && (targetColumn === 'todo' || targetColumn === 'inProgress')) {
      setPendingDropTarget(targetColumn);
      setAssigneeModalOpen(true);
      return;
    }
    if (draggedFrom === 'unassigned' && targetColumn === 'onHold') {
      updateTask(draggedTask.id, { status: 'On Hold', ...actorTask })
        .then((updated) => {
          setUnassigned((prev) => prev.filter((t) => t.id !== draggedTask.id));
          insertInto(setOnHold, updated);
        })
        .catch((err) => {
          setDragError(err instanceof Error ? err.message : 'Failed to move task.');
          setTimeout(() => setDragError(''), 4000);
        });
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }
    if (draggedFrom === 'onHold' && (targetColumn === 'todo' || targetColumn === 'inProgress')) {
      if (!taskHasAssignees(draggedTask)) {
        setPendingDropTarget(targetColumn);
        setAssigneeModalOpen(true);
        return;
      }
      const newStatus = targetColumn === 'todo' ? 'To-Do' : 'In Progress';
      updateTask(draggedTask.id, { status: newStatus, ...actorTask })
        .then((updated) => {
          setOnHold((prev) => prev.filter((t) => t.id !== draggedTask.id));
          insertInto(targetColumn === 'todo' ? setTodo : setInProgress, updated);
        })
        .catch((err) => {
          setDragError(err instanceof Error ? err.message : 'Failed to move task.');
          setTimeout(() => setDragError(''), 4000);
        });
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }
    if (draggedFrom === 'onHold' && targetColumn === 'unassigned') {
      if (taskHasAssignees(draggedTask)) {
        setConfirmUnassignOpen(true);
        return;
      }
      updateTask(draggedTask.id, { status: 'Unassigned', ...actorTask })
        .then((updated) => {
          setOnHold((prev) => prev.filter((t) => t.id !== draggedTask.id));
          insertInto(setUnassigned, updated);
        })
        .catch((err) => {
          setDragError(err instanceof Error ? err.message : 'Failed to move task.');
          setTimeout(() => setDragError(''), 4000);
        });
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }
    if ((draggedFrom === 'todo' || draggedFrom === 'inProgress') && targetColumn === 'onHold') {
      updateTask(draggedTask.id, { status: 'On Hold', ...actorTask })
        .then((updated) => {
          if (draggedFrom === 'todo') setTodo((prev) => prev.filter((t) => t.id !== draggedTask.id));
          else setInProgress((prev) => prev.filter((t) => t.id !== draggedTask.id));
          insertInto(setOnHold, updated);
        })
        .catch((err) => {
          setDragError(err instanceof Error ? err.message : 'Failed to move task.');
          setTimeout(() => setDragError(''), 4000);
        });
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }
    if ((draggedFrom === 'todo' || draggedFrom === 'inProgress') && targetColumn === 'unassigned') {
      setConfirmUnassignOpen(true);
      return;
    }

    // Same column: local reorder only
    if (draggedFrom === targetColumn) {
      const setter =
        targetColumn === 'todo' ? setTodo :
        targetColumn === 'inProgress' ? setInProgress :
        targetColumn === 'onHold' ? setOnHold : setUnassigned;
      setter((prev) => {
        const list = [...prev];
        const fromIdx = list.findIndex((t) => t.id === draggedTask.id);
        if (fromIdx === -1) return prev;
        const [removed] = list.splice(fromIdx, 1);
        const toIdx = list.findIndex((t) => t.id === targetTask.id);
        if (toIdx === -1) { list.push(removed); return list; }
        list.splice(after ? toIdx + 1 : toIdx, 0, removed);
        return list;
      });
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }

    // To-Do ↔ In Progress: API status update + positional insert
    if (!((draggedFrom === 'todo' || draggedFrom === 'inProgress') && (targetColumn === 'todo' || targetColumn === 'inProgress'))) {
      setDraggedTask(null);
      setDraggedFrom(null);
      return;
    }
    const newStatus = targetColumn === 'todo' ? 'To-Do' : 'In Progress';
    updateTask(draggedTask.id, { status: newStatus, ...actorTask })
      .then((updated) => {
        if (draggedFrom === 'todo') setTodo((prev) => prev.filter((t) => t.id !== draggedTask.id));
        else setInProgress((prev) => prev.filter((t) => t.id !== draggedTask.id));
        insertInto(targetColumn === 'todo' ? setTodo : setInProgress, updated);
      })
      .catch((err) => {
        setDragError(err instanceof Error ? err.message : 'Failed to move task.');
        setTimeout(() => setDragError(''), 4000);
      });
    setDraggedTask(null);
    setDraggedFrom(null);
  };

  const handleConfirmAssign = async () => {
    if (!draggedTask || !pendingDropTarget || !user?.id) return;
    const newStatus = pendingDropTarget === 'todo' ? 'To-Do' : 'In Progress';
    try {
      const updated = await updateTask(draggedTask.id, {
        status: newStatus,
        assigned_to_user_ids: [user.id],
        assigned_by_user_id: user.id,
        ...actorTask,
      });
      if (draggedFrom === 'onHold') {
        setOnHold((prev) => prev.filter((t) => t.id !== draggedTask.id));
      } else {
        setUnassigned((prev) => prev.filter((t) => t.id !== draggedTask.id));
      }
      if (pendingDropTarget === 'todo') setTodo((prev) => [...prev, updated]);
      else setInProgress((prev) => [...prev, updated]);
    } catch (err) {
      setDragError(err instanceof Error ? err.message : 'Failed to assign task.');
      setTimeout(() => setDragError(''), 4000);
    }
    setAssigneeModalOpen(false);
    setPendingDropTarget(null);
    setDraggedTask(null);
    setDraggedFrom(null);
  };

  const handleCancelAssign = () => {
    setAssigneeModalOpen(false);
    setPendingDropTarget(null);
    setDraggedTask(null);
    setDraggedFrom(null);
  };

  const handleConfirmUnassign = async () => {
    if (!draggedTask) return;
    try {
      const updated = await updateTask(draggedTask.id, {
        status: 'Unassigned',
        assigned_to_user_ids: [],
        ...actorTask,
      });
      if (draggedFrom === 'onHold') {
        setOnHold((prev) => prev.filter((t) => t.id !== draggedTask.id));
      } else if (draggedFrom === 'todo') {
        setTodo((prev) => prev.filter((t) => t.id !== draggedTask.id));
      } else {
        setInProgress((prev) => prev.filter((t) => t.id !== draggedTask.id));
      }
      setUnassigned((prev) => [...prev, updated]);
    } catch (err) {
      setDragError(err instanceof Error ? err.message : 'Failed to unassign task.');
      setTimeout(() => setDragError(''), 4000);
    }
    setConfirmUnassignOpen(false);
    setDraggedTask(null);
    setDraggedFrom(null);
  };

  const handleCancelUnassign = () => {
    setConfirmUnassignOpen(false);
    setDraggedTask(null);
    setDraggedFrom(null);
  };

  // ── End drag-and-drop handlers ─────────────────────────────────────────────

  // Orders assigned to user: map to To-Do (pending / contract not approved) vs In Progress (in progress, approved, pilot, or delivery/installation)
  const todoOrders = ordersForUser.filter(
    (o) =>
      o.status === 'Pending' ||
      (o.category === 'Contract' && o.status !== 'Approved' && o.status !== 'Pilot')
  );
  const inProgressOrders = ordersForUser.filter(
    (o) =>
      o.status === 'In Progress' ||
      o.status === 'Approved' ||
      o.status === 'Pilot' ||
      o.category === 'Inventory' ||
      o.category === 'Installation'
  );

  // Apply drag-override: which column to show each order in (visual only; order status unchanged)
  const todoColumnOrders = useMemo(() => {
    const defaultTodo = (o: OrderRow) => todoOrders.includes(o);
    const override = (o: OrderRow) => orderColumnOverrides[o.order_number];
    return [
      ...todoOrders.filter((o) => (override(o) ?? 'todo') === 'todo'),
      ...inProgressOrders.filter((o) => (override(o) ?? 'inProgress') === 'todo'),
    ];
  }, [todoOrders, inProgressOrders, orderColumnOverrides]);
  const inProgressColumnOrders = useMemo(() => {
    const override = (o: OrderRow) => orderColumnOverrides[o.order_number];
    return [
      ...inProgressOrders.filter((o) => (override(o) ?? 'inProgress') === 'inProgress'),
      ...todoOrders.filter((o) => (override(o) ?? 'todo') === 'inProgress'),
    ];
  }, [todoOrders, inProgressOrders, orderColumnOverrides]);

  /** Void, completed, and on-hold never appear in the three main columns (on-hold has its own card). */
  const isBoardColumnStatus = (t: TaskRow) =>
    t.status !== 'Void' && t.status !== 'Completed' && t.status !== 'On Hold';

  const todoForBoard = useMemo(() => todo.filter(isBoardColumnStatus), [todo]);
  const inProgressForBoard = useMemo(() => inProgress.filter(isBoardColumnStatus), [inProgress]);
  const unassignedForBoard = useMemo(() => unassigned.filter(isBoardColumnStatus), [unassigned]);

  /** Overdue tasks appear only in the Overdue row, not duplicated in To-Do / In Progress / Unassigned. */
  const todoForBoardColumn = useMemo(
    () => todoForBoard.filter((t) => !isTaskOverdue(t)),
    [todoForBoard]
  );
  const inProgressForBoardColumn = useMemo(
    () => inProgressForBoard.filter((t) => !isTaskOverdue(t)),
    [inProgressForBoard]
  );
  const unassignedForBoardColumn = useMemo(
    () => unassignedForBoard.filter((t) => !isTaskOverdue(t)),
    [unassignedForBoard]
  );

  const filteredTodo = todoForBoardColumn.filter((t) => matchesSearch(t, taskSearch));
  const filteredInProgress = inProgressForBoardColumn.filter((t) => matchesSearch(t, taskSearch));
  const filteredUnassigned = unassignedForBoardColumn.filter((t) => matchesSearch(t, taskSearch));
  const filteredCompleted = completed.filter((t) => matchesSearch(t, taskSearch));

  const overdueTasks = useMemo(() => {
    const uid = user?.id;
    const merged = [...todoForBoard, ...inProgressForBoard, ...unassignedForBoard].filter((t) => isTaskOverdue(t));
    if (uid == null) return merged;
    return merged.filter((t) => taskIsMineForBoardOverdue(t, uid));
  }, [todoForBoard, inProgressForBoard, unassignedForBoard, user?.id]);
  const filteredOverdue = overdueTasks.filter((t) => matchesSearch(t, taskSearch));
  const filteredOnHold = onHold.filter((t) => matchesSearch(t, taskSearch));
  const filteredWatched = watched.filter((t) => matchesSearch(t, taskSearch));

  /** Middle Task Board table: active work only (not completed/void), excluding tasks already in Kanban columns. */
  const boardTasksActiveOnly = useMemo(() => {
    const uid = user?.id;
    // The personal columns also carry tasks the user only created and handed to someone else.
    // Those are still a teammate's work, so subtract just the ones assigned to the user.
    const ownWorkInColumns = [...todo, ...inProgress, ...onHold].filter(
      (t) => uid == null || taskIsAssignedToUser(t, uid)
    );
    const kanbanIds = new Set([
      ...ownWorkInColumns.map((t) => t.id),
      ...unassigned.map((t) => t.id),
      ...watched.map((t) => t.id),
    ]);
    return boardTasks.filter(
      (t) =>
        t.status !== 'Completed' &&
        t.status !== 'Void' &&
        !kanbanIds.has(t.id)
    );
  }, [boardTasks, todo, inProgress, unassigned, onHold, watched, user?.id]);

  const sortedBoardTasksActiveOnly = useMemo(
    () =>
      sortRows(boardTasksActiveOnly, boardTableSort.sortKey, boardTableSort.sortDir, (task, key) => {
        switch (key) {
          case 'name':
            return task.name ?? '';
          case 'status':
            return `${task.status ?? ''}\t${isTaskOverdue(task) ? '0' : '1'}`;
          case 'priority':
            return displayTaskPriority(task, hasRied) ?? '';
          case 'tags':
            return (task.tags ?? []).join(', ');
          case 'assigned_to':
            return task.assigned_to_name ?? '';
          case 'assigned_by':
            return task.assigner_name ?? '';
          case 'company':
            return task.client_company ?? '';
          case 'due_date':
            return task.due_date ?? '';
          default:
            return '';
        }
      }),
    [boardTasksActiveOnly, boardTableSort.sortKey, boardTableSort.sortDir, hasRied]
  );

  const sortedCompleted = useMemo(
    () =>
      sortRows(completed, completedSectionSort.sortKey, completedSectionSort.sortDir, (task, key) => {
        switch (key) {
          case 'id':
            return task.id;
          case 'name':
            return task.name ?? '';
          case 'status':
            return `${task.status ?? ''}\t${isTaskOverdue(task) ? '0' : '1'}`;
          case 'priority':
            return displayTaskPriority(task, hasRied) ?? '';
          case 'project':
            return task.project_name ?? '';
          default:
            return '';
        }
      }),
    [completed, completedSectionSort.sortKey, completedSectionSort.sortDir, hasRied]
  );

  const sortedAssignedCompanies = useMemo(
    () =>
      sortRows(assignedCompanies, assignedCompaniesSort.sortKey, assignedCompaniesSort.sortDir, (c, key) => {
        switch (key) {
          case 'company':
            return c.company ?? '';
          case 'employee':
            return c.employee_name ?? '';
          case 'poc':
            return c.point_of_contact ?? '';
          case 'product':
            return c.product ?? '';
          default:
            return '';
        }
      }),
    [assignedCompanies, assignedCompaniesSort.sortKey, assignedCompaniesSort.sortDir]
  );

  const searchActive = taskSearch.trim().length > 0;

  const sortedSearchResults = useMemo(() => {
    const searchResults: { task: TaskRow; section: string }[] = searchActive
      ? [
          ...filteredTodo.map((task) => ({ task, section: 'To-Do' })),
          ...filteredInProgress.map((task) => ({ task, section: 'In Progress' })),
          ...filteredUnassigned.map((task) => ({ task, section: 'Unassigned' })),
          ...filteredOverdue.map((task) => ({ task, section: 'Overdue' })),
          ...filteredOnHold.map((task) => ({ task, section: 'On Hold' })),
          ...filteredWatched.map((task) => ({ task, section: 'Watched Tasks' })),
          ...filteredCompleted.map((task) => ({
            task,
            section: task.status === 'Void' ? 'Void' : 'Completed',
          })),
        ]
      : [];
    return sortRows(searchResults, searchSort.sortKey, searchSort.sortDir, (row, key) => {
      switch (key) {
        case 'id':
          return row.task.id;
        case 'name':
          return row.task.name ?? '';
        case 'section':
          return row.section;
        case 'status':
          return `${row.task.status ?? ''}\t${isTaskOverdue(row.task) ? '0' : '1'}`;
        case 'priority':
          return displayTaskPriority(row.task, hasRied) ?? '';
        default:
          return '';
      }
    });
  }, [
    searchActive,
    filteredTodo,
    filteredInProgress,
    filteredUnassigned,
    filteredOverdue,
    filteredOnHold,
    filteredWatched,
    filteredCompleted,
    searchSort.sortKey,
    searchSort.sortDir,
    hasRied,
  ]);

  const boardColumnForTask = (taskId: number): BoardColumn => {
    if (onHold.some((t) => t.id === taskId)) return 'onHold';
    if (todo.some((t) => t.id === taskId)) return 'todo';
    if (inProgress.some((t) => t.id === taskId)) return 'inProgress';
    if (watched.some((t) => t.id === taskId)) return 'watched';
    return 'unassigned';
  };

  const renderTaskRow = (task: TaskRow, column: BoardColumn, opts?: { showDueLine?: boolean; disableDrag?: boolean }) => {
    const disableDrag = opts?.disableDrag === true;
    const dispP = displayTaskPriority(task, hasRied);
    return (
    <div
      key={task.id}
      className={[
        'clickable-item tasks-board-item',
        disableDrag ? 'tasks-board-item--no-drag' : 'tasks-board-item--draggable',
        opts?.showDueLine ? 'tasks-board-item--overdue' : '',
        draggedTask?.id === task.id ? 'tasks-board-item--dragging' : '',
        dragOverTaskId === task.id && !dragInsertAfter ? 'tasks-board-item--insert-before' : '',
        dragOverTaskId === task.id && dragInsertAfter ? 'tasks-board-item--insert-after' : '',
      ].filter(Boolean).join(' ')}
      draggable={!disableDrag}
      onDragStart={disableDrag ? undefined : (e) => handleDragStart(e, task, column)}
      onDragEnd={handleDragEnd}
      onDragOver={disableDrag ? undefined : (e) => handleTaskDragOver(e, task, column)}
      onDrop={disableDrag ? undefined : (e) => handleTaskDrop(e, task, column)}
      onClick={() => { if (!isDraggingRef.current) handleTaskClick(task.id); }}
    >
      <span className="tasks-item-drag-handle" aria-hidden="true">⠿</span>
      <div className="tasks-item-body">
        <span className="tasks-item-name">
          {task.name}
          {dispP ? (
            <span
              className="tasks-tag-pill"
              style={{ backgroundColor: PRIORITY_PILL_COLORS[dispP] ?? '#e5e7eb' }}
            >
              {dispP}
              {hasRied && task.ried_priority && task.ried_priority === dispP ? (
                <span className="tasks-board-ried-mark" title="Your Ried priority">
                  {' '}
                  (Ried)
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
        {task.tags?.length > 0 && (
          <span className="tasks-item-tags">
            {task.tags.map((tag) => (
              <span
                key={tag}
                className="tasks-tag-pill"
                style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#8A8F93' }}
              >
                {TAG_LABELS[tag] ?? tag.replace(/_/g, ' ')}
              </span>
            ))}
          </span>
        )}
        {opts?.showDueLine && task.due_date && (
          <span className="tasks-item-due-overdue">Due {formatTaskDate(task.due_date)}</span>
        )}
      </div>
    </div>
    );
  };

  const renderTaskList = (tasks: TaskRow[], column: BoardColumn, opts?: { showDueLine?: boolean; disableDrag?: boolean }) => (
    <div className="tasks-card-content">
      {tasks.length === 0 ? (
        <p className="tasks-empty-hint">No tasks</p>
      ) : (
        tasks.map((task) => renderTaskRow(task, column, opts ?? undefined))
      )}
    </div>
  );

  const orderStageLabel = (category: string) =>
    category === 'Contract'
      ? 'Contract'
      : category === 'Inventory'
        ? 'Invoice'
        : category === 'Installation'
          ? 'Installation'
          : category;

  const renderOrderList = (orders: OrderRow[], column: 'todo' | 'inProgress') => (
    <div className="tasks-card-content">
      {orders.length === 0 ? null : (
        orders.map((order) => (
          <div
            key={order.order_number}
            className={[
              'clickable-item tasks-board-item tasks-board-order-item tasks-board-item--draggable',
              draggedOrder?.order_number === order.order_number ? 'tasks-board-item--dragging' : '',
              dragOverOrderNumber === order.order_number && !dragInsertAfterOrder ? 'tasks-board-item--insert-before' : '',
              dragOverOrderNumber === order.order_number && dragInsertAfterOrder ? 'tasks-board-item--insert-after' : '',
            ].filter(Boolean).join(' ')}
            draggable
            onDragStart={(e) => handleOrderDragStart(e, order, column)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleOrderDragOver(e, order, column)}
            onDrop={(e) => handleOrderDrop(e, column)}
            onClick={() => { if (!isDraggingRef.current) handleOrderClick(order.order_number); }}
          >
            <span className="tasks-item-drag-handle" aria-hidden="true">⠿</span>
            <div className="tasks-item-body">
              <span className="tasks-item-name">
                Order {order.order_number} — {order.company_name}
              </span>
              <span className="tasks-item-tags">
                <span className="tasks-tag-pill" style={{ backgroundColor: '#dbeafe' }} title="Stage">
                  {orderStageLabel(order.category)}
                </span>
                <span
                  className="tasks-tag-pill"
                  style={{
                    backgroundColor: ORDER_STATUS_PILL_COLORS[order.status]?.background ?? '#dbeafe',
                    color: ORDER_STATUS_PILL_COLORS[order.status]?.color ?? '#1B1C1E',
                  }}
                >
                  {order.status}
                </span>
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">

          <div>
            <PageHeader title="My Tasks" subtitle="Manage and track your tasks" />
          </div>
          
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view and manage tasks.</p>
            ) : error ? (
              <p className="create-order-error" role="alert">{error}</p>
            ) : loading ? (
              <p className="page-subtitle">Loading tasks…</p>
            ) : (
              <>
                <div className="page-toolbar">
                  <input
                    type="text"
                    className="page-toolbar-search"
                    placeholder="Search by task name or task number (#123)..."
                    value={taskSearch}
                    onChange={(e) => setTaskSearch(e.target.value)}
                    aria-label="Search tasks"
                  />
                  <button type="button" className="page-toolbar-btn" onClick={handleAddTask}>
                    +&nbsp; Add Task
                  </button>
                </div>
                {searchActive && (
                  <div className="tasks-search-results-section">
                    <table className="tasks-search-results-table">
                      <thead>
                        <tr>
                          <SortableTh label="Task #" columnKey="id" sortKey={searchSort.sortKey} sortDir={searchSort.sortDir} onSort={searchSort.toggleSort} />
                          <SortableTh label="Task Name" columnKey="name" sortKey={searchSort.sortKey} sortDir={searchSort.sortDir} onSort={searchSort.toggleSort} />
                          <SortableTh label="Section" columnKey="section" sortKey={searchSort.sortKey} sortDir={searchSort.sortDir} onSort={searchSort.toggleSort} />
                          <SortableTh label="Status" columnKey="status" sortKey={searchSort.sortKey} sortDir={searchSort.sortDir} onSort={searchSort.toggleSort} />
                          <SortableTh label="Priority" columnKey="priority" sortKey={searchSort.sortKey} sortDir={searchSort.sortDir} onSort={searchSort.toggleSort} />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSearchResults.length === 0 ? (
                          <tr>
                            <td colSpan={5} data-label="">No tasks match your search</td>
                          </tr>
                        ) : (
                          sortedSearchResults.map(({ task, section }) => (
                            <tr
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className="tasks-search-result-row"
                            >
                              <td data-label="Task #">#{task.id}</td>
                              <td data-label="Task Name">{task.name}</td>
                              <td data-label="Section">{section}</td>
                              <td data-label="Status">
                                <TaskStatusPills task={task} />
                              </td>
                              <td data-label="Priority">
                                {(() => {
                                  const p = displayTaskPriority(task, hasRied);
                                  if (!p) return '—';
                                  return (
                                    <span
                                      className="tasks-tag-pill"
                                      style={{ backgroundColor: PRIORITY_PILL_COLORS[p] ?? '#e5e7eb' }}
                                      title={hasRied && task.ried_priority && p === task.ried_priority ? 'Your Ried priority' : undefined}
                                    >
                                      {p}
                                      {hasRied && task.ried_priority && p === task.ried_priority ? ' (Ried)' : ''}
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                {dragError && (
                  <p className="tasks-drag-error" role="alert">{dragError}</p>
                )}

                <h3 className="completed-tasks-title">Your Task Board</h3>
                <div className="tasks-board-top-block">
                  <div className="tasks-board-cards">
                    <div
                      className={`tasks-board-card${dragOverColumn === 'todo' ? ' tasks-board-card--drag-over' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'todo')}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, 'todo')}
                    >
                      <h3 className="tasks-card-title">To-Do:</h3>
                      {todoForBoardColumn.length > 0 || todoColumnOrders.length > 0 ? (
                        <>
                          {todoForBoardColumn.length > 0 && renderTaskList(todoForBoardColumn, 'todo')}
                          {todoForBoardColumn.length > 0 && todoColumnOrders.length > 0 && <div className="tasks-board-divider" />}
                          {todoColumnOrders.length > 0 && renderOrderList(todoColumnOrders, 'todo')}
                        </>
                      ) : (
                        <p className="tasks-empty-hint">Drop tasks here or no tasks</p>
                      )}
                    </div>
                    <div
                      className={`tasks-board-card${dragOverColumn === 'inProgress' ? ' tasks-board-card--drag-over' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'inProgress')}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, 'inProgress')}
                    >
                      <h3 className="tasks-card-title">In Progress:</h3>
                      {inProgressForBoardColumn.length > 0 || inProgressColumnOrders.length > 0 ? (
                        <>
                          {inProgressForBoardColumn.length > 0 && renderTaskList(inProgressForBoardColumn, 'inProgress')}
                          {inProgressForBoardColumn.length > 0 && inProgressColumnOrders.length > 0 && <div className="tasks-board-divider" />}
                          {inProgressColumnOrders.length > 0 && renderOrderList(inProgressColumnOrders, 'inProgress')}
                        </>
                      ) : (
                        <p className="tasks-empty-hint">Drop tasks here or no tasks</p>
                      )}
                    </div>
                    <div
                      className={`tasks-board-card${dragOverColumn === 'unassigned' ? ' tasks-board-card--drag-over' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'unassigned')}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, 'unassigned')}
                    >
                      <h3 className="tasks-card-title">Unassigned Tasks:</h3>
                      {renderTaskList(unassignedForBoardColumn, 'unassigned')}
                    </div>
                  </div>

                  <div className="tasks-board-overdue-row">
                    <div className="tasks-board-card tasks-board-card--overdue">
                      <h3 className="tasks-card-title">Overdue:</h3>
                      {filteredOverdue.length === 0 ? (
                        <p className="tasks-empty-hint">No overdue tasks</p>
                      ) : (
                        <div className="tasks-card-content">
                          {filteredOverdue.map((task) =>
                            renderTaskRow(task, boardColumnForTask(task.id), { showDueLine: true })
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className={[
                        'tasks-board-card tasks-board-card--on-hold',
                        dragOverColumn === 'onHold' ? ' tasks-board-card--drag-over' : '',
                      ].filter(Boolean).join('')}
                      onDragOver={handleOnHoldDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, 'onHold')}
                    >
                      <h3 className="tasks-card-title">On Hold:</h3>
                      {renderTaskList(filteredOnHold, 'onHold')}
                    </div>
                    <div
                      className="tasks-board-card tasks-board-card--watched"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (draggedOrder) e.dataTransfer.dropEffect = 'none';
                      }}
                    >
                      <h3 className="tasks-card-title">Watched Tasks:</h3>
                      {renderTaskList(filteredWatched, 'watched', { disableDrag: true })}
                    </div>
                  </div>
                </div>

                <div className="task-board-section">
                  <div className="collapsible-header" onClick={() => toggleSection('taskBoard')}>
                    <h3 className="completed-tasks-title">Task Board</h3>
                    <span className={`collapse-arrow${!collapsedSections.taskBoard ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.taskBoard && (
                    <div className="collapsible-table-wrapper">
                      <table className="all-tasks-table">
                        <thead>
                          <tr>
                            <SortableTh label="Task" columnKey="name" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Status" columnKey="status" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Priority" columnKey="priority" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Roles" columnKey="tags" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Assigned to" columnKey="assigned_to" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Assigned by" columnKey="assigned_by" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Company" columnKey="company" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                            <SortableTh label="Due date" columnKey="due_date" sortKey={boardTableSort.sortKey} sortDir={boardTableSort.sortDir} onSort={boardTableSort.toggleSort} />
                          </tr>
                        </thead>
                        <tbody>
                          {boardTasksError ? (
                            <tr>
                              <td colSpan={8} data-label="" role="alert">{boardTasksError}</td>
                            </tr>
                          ) : sortedBoardTasksActiveOnly.length === 0 ? (
                            <tr>
                              <td colSpan={8} data-label="">No tasks that share your roles (excluding your to-dos, in progress, unassigned, on hold, completed, and void).</td>
                            </tr>
                          ) : (
                            sortedBoardTasksActiveOnly.map((task) => (
                              <tr key={task.id} className="all-tasks-row" onClick={() => handleTaskClick(task.id)}>
                                <td data-label="Task">{task.name}</td>
                                <td data-label="Status">
                                  <TaskStatusPills task={task} />
                                </td>
                                <td data-label="Priority">
                                  {(() => {
                                    const p = displayTaskPriority(task, hasRied);
                                    if (!p) return '—';
                                    return (
                                      <span
                                        className="dashboard-pill"
                                        style={{ backgroundColor: PRIORITY_PILL_COLORS[p] ?? '#e5e7eb' }}
                                        title={hasRied && task.ried_priority && p === task.ried_priority ? 'Your Ried priority' : undefined}
                                      >
                                        {p}
                                        {hasRied && task.ried_priority && p === task.ried_priority ? ' (Ried)' : ''}
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td data-label="Roles">
                                  <div className="dashboard-admin-tasks-tags">
                                    {(task.tags?.length ? task.tags : []).map((tag) => (
                                      <span
                                        key={tag}
                                        className="dashboard-pill"
                                        style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#e5e7eb' }}
                                      >
                                        {TAG_LABELS[tag] ?? tag.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                    {(!task.tags || task.tags.length === 0) && '—'}
                                  </div>
                                </td>
                                <td data-label="Assigned to">{task.assigned_to_name ?? '—'}</td>
                                <td data-label="Assigned by">{task.assigner_name ?? '—'}</td>
                                <td data-label="Company">{task.client_company ?? '—'}</td>
                                <td data-label="Due date">{formatTaskDate(task.due_date)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="completed-tasks-section">
                  <div className="collapsible-header" onClick={() => toggleSection('completedTasks')}>
                    <h3 className="completed-tasks-title">Completed &amp; void</h3>
                    <span className={`collapse-arrow${!collapsedSections.completedTasks ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.completedTasks && <>
                  <div className="collapsible-table-wrapper"><table className="completed-tasks-table">
                    <thead>
                      <tr>
                        <SortableTh label="Task #" columnKey="id" sortKey={completedSectionSort.sortKey} sortDir={completedSectionSort.sortDir} onSort={completedSectionSort.toggleSort} />
                        <SortableTh label="Task Name" columnKey="name" sortKey={completedSectionSort.sortKey} sortDir={completedSectionSort.sortDir} onSort={completedSectionSort.toggleSort} />
                        <SortableTh label="Status" columnKey="status" sortKey={completedSectionSort.sortKey} sortDir={completedSectionSort.sortDir} onSort={completedSectionSort.toggleSort} />
                        <SortableTh label="Priority" columnKey="priority" sortKey={completedSectionSort.sortKey} sortDir={completedSectionSort.sortDir} onSort={completedSectionSort.toggleSort} />
                        <SortableTh label="Project" columnKey="project" sortKey={completedSectionSort.sortKey} sortDir={completedSectionSort.sortDir} onSort={completedSectionSort.toggleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCompleted.length === 0 ? (
                        <tr>
                          <td colSpan={5} data-label="">No completed or void tasks</td>
                        </tr>
                      ) : (
                        sortedCompleted.map((task) => (
                          <tr
                            key={task.id}
                            onClick={() => handleTaskClick(task.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td data-label="Task #">#{task.id}</td>
                            <td data-label="Task Name">{task.name}</td>
                            <td data-label="Status">
                              <TaskStatusPills task={task} pillClassName="tasks-tag-pill" />
                            </td>
                            <td data-label="Priority">
                              {(() => {
                                const p = displayTaskPriority(task, hasRied);
                                if (!p) return '—';
                                return (
                                  <span
                                    className="tasks-tag-pill"
                                    style={{ backgroundColor: PRIORITY_PILL_COLORS[p] ?? '#e5e7eb' }}
                                    title={hasRied && task.ried_priority && p === task.ried_priority ? 'Your Ried priority' : undefined}
                                  >
                                    {p}
                                    {hasRied && task.ried_priority && p === task.ried_priority ? ' (Ried)' : ''}
                                  </span>
                                );
                              })()}
                            </td>
                            <td data-label="Project">
                              {task.project_id != null && task.project_name ? (
                                <Link
                                  to={`/projects/${task.project_id}`}
                                  className="tasks-board-project-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {task.project_name}
                                </Link>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table></div>
                  </>}
                </div>

                {isAdmin && (
                  <div className="all-tasks-section">
                    <div className="collapsible-header" onClick={() => toggleSection('allTasks')}>
                      <h3 className="all-tasks-title">All Tasks</h3>
                      <span className={`collapse-arrow${!collapsedSections.allTasks ? ' collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    {!collapsedSections.allTasks && (
                      <div className="collapsible-table-wrapper">
                        <table className="all-tasks-table">
                          <thead>
                            <tr>
                              <SortableTh label="Task" columnKey="name" sortKey={allTasksSort.sortKey} sortDir={allTasksSort.sortDir} onSort={allTasksSort.toggleSort} />
                              {(
                                [
                                  'status',
                                  'priority',
                                  'tags',
                                  'assigned_to',
                                  'assigned_by',
                                  'company',
                                  'project',
                                  'due_date',
                                ] as const
                              ).map((col) => {
                                const labels: Record<string, string> = {
                                  status: 'Status',
                                  priority: 'Priority',
                                  tags: 'Roles',
                                  assigned_to: 'Assigned to',
                                  assigned_by: 'Assigned by',
                                  company: 'Company',
                                  project: 'Project',
                                  due_date: 'Due date',
                                };
                                const isActive = allTasksFilters[col].length > 0;
                                return (
                                  <th key={col}>
                                    <div className="all-tasks-th-content">
                                      <SortHeaderButton
                                        label={labels[col]}
                                        columnKey={col}
                                        sortKey={allTasksSort.sortKey}
                                        sortDir={allTasksSort.sortDir}
                                        onSort={allTasksSort.toggleSort}
                                      />
                                      <button
                                        className={`all-tasks-filter-btn${isActive ? ' active' : ''}`}
                                        onClick={(e) => handleFilterBtnClick(col, e)}
                                        title={`Filter by ${labels[col]}`}
                                      >
                                        ▾
                                      </button>
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedFilteredAllTasks.length === 0 ? (
                              <tr>
                                <td colSpan={9} data-label="">
                                  {allTasks.length === 0 ? 'No tasks.' : 'No tasks match the current filters.'}
                                </td>
                              </tr>
                            ) : (
                              sortedFilteredAllTasks.map((task) => (
                                <tr key={task.id} className="all-tasks-row" onClick={() => handleTaskClick(task.id)}>
                                  <td data-label="Task">{task.name}</td>
                                  <td data-label="Status">
                                    <TaskStatusPills task={task} />
                                  </td>
                                  <td data-label="Priority">
                                    {(() => {
                                      const p = displayTaskPriority(task, hasRied);
                                      if (!p) return '—';
                                      return (
                                        <span
                                          className="dashboard-pill"
                                          style={{ backgroundColor: PRIORITY_PILL_COLORS[p] ?? '#e5e7eb' }}
                                          title={hasRied && task.ried_priority && p === task.ried_priority ? 'Your Ried priority' : undefined}
                                        >
                                          {p}
                                          {hasRied && task.ried_priority && p === task.ried_priority ? ' (Ried)' : ''}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td data-label="Roles">
                                    <div className="dashboard-admin-tasks-tags">
                                      {(task.tags?.length ? task.tags : []).map((tag) => (
                                        <span
                                          key={tag}
                                          className="dashboard-pill"
                                          style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#e5e7eb' }}
                                        >
                                          {TAG_LABELS[tag] ?? tag.replace(/_/g, ' ')}
                                        </span>
                                      ))}
                                      {(!task.tags || task.tags.length === 0) && '—'}
                                    </div>
                                  </td>
                                  <td data-label="Assigned to">{task.assigned_to_name ?? '—'}</td>
                                  <td data-label="Assigned by">{task.assigner_name ?? '—'}</td>
                                  <td data-label="Company">{task.client_company ?? '—'}</td>
                                  <td data-label="Project">
                                    {task.project_id != null && task.project_name ? (
                                      <Link
                                        to={`/projects/${task.project_id}`}
                                        className="tasks-board-project-link"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {task.project_name}
                                      </Link>
                                    ) : (
                                      '—'
                                    )}
                                  </td>
                                  <td data-label="Due date">{formatTaskDate(task.due_date)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <div className="assigned-companies-section">
                  <div className="collapsible-header" onClick={() => toggleSection('assignedCompanies')}>
                    <h3 className="assigned-companies-title">Assigned Companies</h3>
                    <span className={`collapse-arrow${!collapsedSections.assignedCompanies ? ' collapse-arrow--open' : ''}`}>▶</span>
                  </div>
                  {!collapsedSections.assignedCompanies && <>
                  <div className="collapsible-table-wrapper"><table className="assigned-companies-table">
                    <thead>
                      <tr>
                        <SortableTh label="Company Name" columnKey="company" sortKey={assignedCompaniesSort.sortKey} sortDir={assignedCompaniesSort.sortDir} onSort={assignedCompaniesSort.toggleSort} />
                        <SortableTh label="Employee" columnKey="employee" sortKey={assignedCompaniesSort.sortKey} sortDir={assignedCompaniesSort.sortDir} onSort={assignedCompaniesSort.toggleSort} />
                        <SortableTh label="Point of Contact" columnKey="poc" sortKey={assignedCompaniesSort.sortKey} sortDir={assignedCompaniesSort.sortDir} onSort={assignedCompaniesSort.toggleSort} />
                        <SortableTh label="Product" columnKey="product" sortKey={assignedCompaniesSort.sortKey} sortDir={assignedCompaniesSort.sortDir} onSort={assignedCompaniesSort.toggleSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAssignedCompanies.length === 0 ? (
                        <tr>
                          <td colSpan={4} data-label="">No clients assigned to you</td>
                        </tr>
                      ) : (
                        sortedAssignedCompanies.map((client) => (
                          <tr
                            key={client.id}
                            onClick={() => handleClientClick(client.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td data-label="Company">{client.company}</td>
                            <td data-label="Employee">{client.employee_name ?? '—'}</td>
                            <td data-label="Point of Contact">{client.point_of_contact}</td>
                            <td data-label="Product">{client.product ?? '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table></div>
                  </>}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <Modal
        isOpen={addTaskModalOpen}
        onClose={() => setAddTaskModalOpen(false)}
        title="Add New Task"
        wide
      >
        <AddTaskForm onClose={() => setAddTaskModalOpen(false)} />
      </Modal>

      {/* Confirm assign modal: shown when dropping an unassigned task onto To-Do or In Progress */}
      <Modal
        isOpen={assigneeModalOpen}
        onClose={handleCancelAssign}
        title="Assign Task?"
        narrow
      >
        <div className="tasks-modal-body">
          <p className="tasks-modal-desc">
            Moving <strong>{draggedTask?.name}</strong> to{' '}
            <strong>{pendingDropTarget === 'todo' ? 'To-Do' : 'In Progress'}</strong> will assign this task to you ({user?.username}).
          </p>
          <div className="tasks-modal-actions">
            <button type="button" className="tasks-modal-btn tasks-modal-btn--secondary" onClick={handleCancelAssign}>
              Cancel
            </button>
            <button type="button" className="tasks-modal-btn tasks-modal-btn--primary" onClick={handleConfirmAssign}>
              Assign to Me &amp; Move
            </button>
          </div>
        </div>
      </Modal>

      {/* All Tasks column filter dropdown (fixed, escapes table overflow) */}
      {openFilterCol && filterDropdownPos && (() => {
        const col = openFilterCol;
        const vals = allTasksUniqueValues[col as keyof typeof allTasksUniqueValues] ?? [];
        const active = allTasksFilters[col] ?? [];
        return (
          <div
            className="all-tasks-filter-dropdown"
            style={{ top: filterDropdownPos.top, left: filterDropdownPos.left }}
          >
            {vals.length === 0 && <span className="all-tasks-filter-empty">No options</span>}
            {vals.map((val) => (
              <label key={val} className="all-tasks-filter-option">
                <input
                  type="checkbox"
                  checked={active.includes(val)}
                  onChange={() => toggleFilterValue(col, val)}
                />
                {col === 'tags' ? (TAG_LABELS[val] ?? val.replace(/_/g, ' ')) : val}
              </label>
            ))}
            {active.length > 0 && (
              <button
                className="all-tasks-filter-clear"
                onClick={() => setAllTasksFilters((p) => ({ ...p, [col]: [] }))}
              >
                Clear filter
              </button>
            )}
          </div>
        );
      })()}

      {/* Confirm unassign modal: shown when dropping a task back onto Unassigned */}
      <Modal
        isOpen={confirmUnassignOpen}
        onClose={handleCancelUnassign}
        title="Unassign Task?"
        narrow
      >
        <div className="tasks-modal-body">
          <p className="tasks-modal-desc">
            Moving <strong>{draggedTask?.name}</strong> back to <strong>Unassigned</strong> will unassign this task from you ({user?.username}).
          </p>
          <div className="tasks-modal-actions">
            <button type="button" className="tasks-modal-btn tasks-modal-btn--secondary" onClick={handleCancelUnassign}>
              Cancel
            </button>
            <button type="button" className="tasks-modal-btn tasks-modal-btn--danger" onClick={handleConfirmUnassign}>
              Unassign &amp; Move
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TasksBoard;
